/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./DeleteAccount.css";
import { simpleFetch } from "../../restWrapper";
import errorMsg from "../../assets/json/errMsg.json";
import { renderErrorMsg } from "../../assets/component/renderErrorMsg.js";
import { isEmpty } from "../../assets/validators/common.js";
import { getOrganizationID } from "../../assets/helper";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";

class DeleteAccount extends Component {
  constructor(props) {
    super(props);
    const parsedDomain = getOrganizationID(window.location.href);
    this.state = {
      checking: true,
      hasScheduled: false,
      subdomain: parsedDomain.subdomain,
      domain: parsedDomain.domain,
      isCustomDomain: parsedDomain.isCustomDomain,
      password: "",
      submitting: false,
      err: [],
    };

    this._handlePassword = (e) => {
      this.setState({
        password: e.target.value,
      });
    };

    this._onSubmit = () => {
      if (this.state.submitting) return;
      const deleteAccountErr = this.getErrObj();

      if (isEmpty(this.state.password)) {
        this.setState({ err: [deleteAccountErr.wrongPassword] });
        return;
      }

      this.setState({ submitting: true, err: [] });
      this.props
        .deleteAccount({ password: this.state.password })
        .then((res) => {
          if ([200, 204].includes(res.status)) {
            // Keep submitting/loading until navigation completes
            this.props.history.push({ pathname: "/" });
            return;
          }
          let showErrText = [];
          const errors = res.body && res.body.errors;
          if (errors && errors.scheduled) {
            showErrText.push(deleteAccountErr.scheduled);
          } else if (errors && errors.password) {
            showErrText.push(deleteAccountErr.wrongPassword);
          } else {
            showErrText.push(deleteAccountErr.fail);
          }
          this.setState({
            submitting: false,
            err: showErrText,
          });
        })
        .catch(() => {
          this.setState({
            submitting: false,
            err: [deleteAccountErr.fail],
          });
        });
    };
  }

  componentDidMount() {
    simpleFetch("/donors/organization", "GET", {
      subDomain: this.state.isCustomDomain
        ? this.state.domain
        : this.state.subdomain,
      isCustomDomain: this.state.isCustomDomain,
    }).then((res) => {
      if (res.status !== 200 || !res.body) {
        this.setState({ checking: false, err: [this.getErrObj().fail] });
        return;
      }
      // Both statuses represent pending payments the donor must cancel
      // first — the same set DELETE /donors/payments/:paymentId accepts
      Promise.all(
        ["scheduled", "reauthenticate"].map((status) =>
          simpleFetch("/donors/payments", "GET", {
            organizationViewId: res.body.id,
            status: status,
          }),
        ),
      ).then((results) => {
        this.setState({
          checking: false,
          hasScheduled: results.some(
            (res) => res.status === 200 && res.body && res.body.length > 0,
          ),
        });
      });
    });
  }

  getErrObj() {
    const language = this.props.language;
    return (
      (errorMsg[language] && errorMsg[language].DeleteAccount) ||
      errorMsg["en"].DeleteAccount
    );
  }

  renderScheduledBlock(languageObj) {
    return (
      <div className="DeleteAccount-fieldbtn">
        <div className="DeleteAccount-warningTitle">
          {languageObj.scheduledTitle}
        </div>
        <div className="DeleteAccount-scheduledText">
          {languageObj.scheduledText}
        </div>
        <Link to="/DonationRecordRecurring">
          <div className="DeleteAccount-scheduledBtn">
            {languageObj.scheduledBtn}
          </div>
        </Link>
      </div>
    );
  }

  renderConfirmForm(languageObj) {
    return (
      <div className="DeleteAccount-fieldbtn">
        <div className="DeleteAccount-warningTitle">
          {languageObj.warningTitle}
        </div>
        <ul className="DeleteAccount-consequenceList">
          <li>{languageObj.consequence1}</li>
          <li>{languageObj.consequence2}</li>
        </ul>
        <div className="DeleteAccount-receiptHint">
          {languageObj.receiptHint}
        </div>

        <div className="DeleteAccount-inputFieldContainer">
          <input
            type="password"
            onChange={this._handlePassword}
            className="DeleteAccount-inputField"
            placeholder={languageObj.passwordPlaceholder}
            disabled={this.state.submitting}
          />
        </div>
        {this.state.err.map((err) => renderErrorMsg(err))}

        <div
          className={
            this.state.submitting
              ? "DeleteAccount-confirmBtn DeleteAccount-confirmBtnDisabled"
              : "DeleteAccount-confirmBtn"
          }
          onClick={this._onSubmit}
        >
          {this.state.submitting
            ? languageObj.submitting || "Deleting..."
            : languageObj.confirmBtn}
        </div>
      </div>
    );
  }

  render() {
    if (this.state.checking || this.state.submitting) {
      return <this.props.RenderFullPageLoading />;
    }

    if (!this.props.location.state || !this.props.location.state.color) {
      return null;
    }

    const languageObj = this.props.languageObj;

    const backgroundOverrideStyle = {
      background: "#" + this.props.location.state.color.headerColor,
    };

    const color = this.props.location.state.color;

    const organizationName = this.props.location.state.organizationName;

    return (
      <div className="DeleteAccount-view">
        <Helmet>
          <title>{organizationName}</title>
        </Helmet>
        <div className="DeleteAccount-bg" style={backgroundOverrideStyle} />
        <div className="DeleteAccount-container">
          <div className="DeleteAccount-header">
            <Link
              to={{
                pathname: "/AcInfo",
                state: {
                  color: color,
                  organizationName: organizationName,
                },
              }}
            >
              <div className="DeleteAccount-backContainer">
                <div className="DeleteAccount-back">
                  <div className="DeleteAccount-backBtn"></div>
                  <div className="DeleteAccount-backText">
                    {languageObj.backBtn}
                  </div>
                </div>
              </div>
            </Link>

            <div className="DeleteAccount-headingTextContainer">
              <div className="DeleteAccount-headingText">
                {languageObj.headingText}
              </div>
            </div>
          </div>

          <div className="DeleteAccount-main">
            {this.state.hasScheduled
              ? this.renderScheduledBlock(languageObj)
              : this.renderConfirmForm(languageObj)}
          </div>
        </div>
      </div>
    );
  }
}

DeleteAccount.propTypes = {
  language: PropTypes.string,
  languageObj: PropTypes.object,
  deleteAccount: PropTypes.func,
  RenderFullPageLoading: PropTypes.func,
};

export default DeleteAccount;
